/**
 * The button whose label says exactly what it will write, what happens to the rows that do not
 * fit, and the confirm step before a table already in the case is written again.
 */

import type { TFunction } from 'i18next'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import type { LeftOutRows, OutputSummary, ProjectionResult } from '@/services/api/structuredIngest'
import { formatDate } from '@/utils/date'
import { LeftOutRowsNotice } from './LeftOutRows'
import { countPhrase, hasSearchableOutput, type ReviewState, sumValues } from './review-state'

interface WriteBarData {
  extraction?: { selected: boolean; cells: number | null }
  written: ProjectionResult | null
  writtenAt: string | null
  /** Rows the preview says a projection would leave out. */
  leftOut: LeftOutRows | null
  summary: OutputSummary
  review: ReviewState
  projecting: boolean
  pending: boolean
  runError: string | null
}

/** What the button writes, one phrase per kind ("55 entities"); the copy joins them as a list. */
function whatItWrites({ summary, review, extraction }: WriteBarData, t: TFunction): string[] {
  return [
    !review.noEntities && countPhrase(t, 'entities', summary.totals.nodes),
    summary.edges.some(edge => edge.enabled) &&
      countPhrase(t, 'relationships', summary.totals.edges),
    hasSearchableOutput(review.windows) && countPhrase(t, 'snippets', review.windows.count),
    extraction?.selected && t('tableMapping.extraction.output'),
  ].filter((part): part is string => !!part)
}

function description(data: WriteBarData, t: TFunction): string {
  if (data.written) return t('tableMapping.write.descriptionUpdate')
  if (data.review.searchOnly) return t('tableMapping.write.descriptionSearchOnly')
  return t('tableMapping.write.description')
}

function UpdateConfirmation({
  data,
  open,
  onOpenChange,
  onProject,
}: {
  data: WriteBarData & { written: ProjectionResult }
  open: boolean
  onOpenChange: (open: boolean) => void
  onProject: () => void
}) {
  const { t, i18n } = useTranslation()
  const previous = [
    t('tableMapping.count.entities', { count: sumValues(data.written.nodes) }),
    t('tableMapping.count.relationships', { count: sumValues(data.written.edges) }),
  ]
  const writes = whatItWrites(data, t)
  const next = writes.length > 0 ? writes : [t('tableMapping.write.nothing')]
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('tableMapping.write.confirmTitle')}</AlertDialogTitle>
          <AlertDialogDescription>
            {data.writtenAt
              ? t('tableMapping.write.confirmBodyDated', {
                  previous,
                  date: formatDate(data.writtenAt, i18n.language),
                  next,
                })
              : t('tableMapping.write.confirmBody', { previous, next })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-wrap">
          <AlertDialogCancel>{t('tableMapping.write.keep')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              onOpenChange(false)
              onProject()
            }}
          >
            {t(
              data.extraction?.selected
                ? 'tableMapping.write.updateExtract'
                : 'tableMapping.write.update'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export function WriteBar({ data, onProject }: { data: WriteBarData; onProject: () => void }) {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const what = whatItWrites(data, t)
  const verb = data.written ? 'update' : 'add'
  const label = data.extraction?.selected
    ? t(`tableMapping.write.${verb}Extract`)
    : what.length > 0
      ? t(`tableMapping.write.${verb}With`, { what })
      : t(`tableMapping.write.${verb}`)
  const write = () => {
    if (data.written) setConfirmOpen(true)
    else onProject()
  }
  return (
    <>
      {data.leftOut && !data.review.blockedRows && <LeftOutRowsNotice leftOut={data.leftOut} />}
      <div className="flex flex-wrap items-center gap-3 border-t pt-4">
        <Button
          variant="solid"
          className="max-md:h-auto max-md:min-h-10 max-md:w-full max-md:whitespace-normal max-md:py-2 max-md:text-center"
          loading={data.projecting}
          disabled={data.pending || data.review.blockers > 0}
          onClick={write}
        >
          {data.projecting ? t('tableMapping.write.adding') : label}
        </Button>
        <span className="max-w-prose text-xs text-muted-foreground">{description(data, t)}</span>
        {data.extraction?.selected && (
          <span role="status" className="text-xs text-muted-foreground">
            {data.extraction.cells === null
              ? t('tableMapping.extraction.counting')
              : t('tableMapping.extraction.cells', { count: data.extraction.cells })}
          </span>
        )}
        {data.runError && (
          <span className="text-xs text-destructive">
            {t('tableMapping.write.runFailed', { error: data.runError })}
          </span>
        )}
      </div>
      {data.written && data.written.warnings.length > 0 && (
        <ul className="flex list-disc flex-col gap-1 pl-4 text-xs text-destructive">
          {data.written.warnings.map(w => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      {data.written?.leftOut && <LeftOutRowsNotice leftOut={data.written.leftOut} written />}
      {data.written && (
        <UpdateConfirmation
          data={{ ...data, written: data.written }}
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          onProject={onProject}
        />
      )}
    </>
  )
}
